import React, { useState, useEffect, useMemo } from 'react';
import { groupCRMUsersByDTC } from '../../utils/crmUserSorting';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  useToast,
  useDisclosure,
  Spinner,
  Center,
  Text,
  useColorModeValue,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { FiPlus } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useTasks } from '../../hooks/useTasks';
import TaskList from '../../components/Tasks/TaskList';
import UnifiedEventModal from '../../components/CRM/Calendar/UnifiedEventModal';
import TaskDetailModal from '../../components/Tasks/TaskDetailModal';
import TaskCompletionActivityPrompt, { taskHasLoggableEntity } from '../../components/CRM/TaskCompletionActivityPrompt';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../utils/init-firebase';
import moment from 'moment';

export default function TasksPage() {
  const { currentUser, SuperAdmin, loading: authLoading } = useAuth();
  const toast = useToast();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const { isOpen: isActivityPromptOpen, onOpen: onActivityPromptOpen, onClose: onActivityPromptClose } = useDisclosure();
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [completedTaskForPrompt, setCompletedTaskForPrompt] = useState(null);
  const [crmUsers, setCrmUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Build array of user IDs: selected users or current user
  // Special case: if "all" is selected (for SuperAdmins), pass empty array to fetch ALL tasks
  const effectiveUserIds = selectedUsers.some(u => u.value === 'all')
    ? [] // Empty array triggers "view all" mode in useTasks
    : selectedUsers.length > 0 
      ? selectedUsers.map(u => u.value) 
      : [currentUser?.uid];

  // Debug logging to track task fetching
  const isViewingAll = selectedUsers.some(u => u.value === 'all');

  const { tasks, loading, error, completeTask, createTask, deleteTask, reopenTask } = 
    useTasks(effectiveUserIds);

  // Build grouped options for the user Select: DTC LOs first, then others
  const userSelectOptions = useMemo(() => {
    const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers, 'label');
    const groups = [];

    // "All Users" option for SuperAdmins (standalone group above the rest)
    if (SuperAdmin) {
      groups.push({
        label: '',
        options: [{ value: 'all', label: '👥 All Users (View All Tasks)' }],
      });
    }

    if (dtcUsers.length > 0) {
      groups.push({ label: 'DTC Loan Officers', options: dtcUsers });
    }
    groups.push({
      label: dtcUsers.length > 0 ? 'Other Users' : 'Users',
      options: otherUsers,
    });
    return groups;
  }, [crmUsers, SuperAdmin]);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headerBG = useColorModeValue('white', 'gray.800');
  const filterBG = useColorModeValue('gray.50', 'gray.700');

  // Load CRM users for task assignment (formatted for chakra-react-select)
  useEffect(() => {
    const loadCRMUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('CRMUser', '==', true));
        const snapshot = await getDocs(q);
        const userList = [];
        snapshot.forEach(doc => {
          const userData = doc.data();
          userList.push({
            value: doc.id,
            label: userData.Name || userData.name || userData.email,
            isDTCLoanOfficer: userData.isDTCLoanOfficer || false,
            Roles: userData.Roles || [],
          });
        });
        
        setCrmUsers(userList);
      } catch (error) {
        console.error('Error loading CRM users:', error);
      }
    };
    
    loadCRMUsers();
  }, [SuperAdmin]);

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    onDetailOpen();
  };

  // Completing a task linked to a contact/deal offers to log the interaction
  // as a CRM activity. The task is captured before completing because hook
  // refetches can reshuffle the list.
  const handleCompleteTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const result = await completeTask(taskId);
    if (result?.success && taskHasLoggableEntity(task)) {
      setCompletedTaskForPrompt(task);
      onActivityPromptOpen();
    }
    return result;
  };

  const handleActivityPromptClose = () => {
    onActivityPromptClose();
    setCompletedTaskForPrompt(null);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setSelectedTask(null);
    onDetailClose();
    onCreateOpen();
  };

  const handleDeleteTask = async (taskId) => {
    const result = await deleteTask(taskId);
    if (result.success) {
      toast({
        title: 'Task deleted',
        status: 'success',
        duration: 2000,
      });
    }
    return result;
  };

  const handleCloseCreateModal = () => {
    setEditingTask(null);
    onCreateClose();
  };

  // Get user names for display in detail modal
  const getAssignedUserName = (userId) => {
    return crmUsers.find(u => u.value === userId)?.label || 'Unknown User';
  };

  const getCreatedByUserName = (userId) => {
    return crmUsers.find(u => u.value === userId)?.label || 'Unknown User';
  };

  // Wait for both auth and tasks to load
  if (authLoading || loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="60vh" flexDir="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text color="gray.600">{authLoading ? 'Authenticating...' : 'Loading tasks...'}</Text>
        </Center>
      </Container>
    );
  }

  const activeTasks = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  
  // Helper to convert dueDate to moment - handles both Firestore Timestamps and JS Dates
  const getDueDateMoment = (dueDate) => {
    if (!dueDate) return null;
    // Firestore Timestamp has .toDate(), JS Date doesn't need conversion
    const date = dueDate.toDate ? dueDate.toDate() : dueDate;
    return moment(date);
  };
  
  const overdueTasks = activeTasks.filter(t => {
    const dueMoment = getDueDateMoment(t.dueDate);
    if (!dueMoment) return false;
    return dueMoment.isBefore(moment(), 'day');
  });
  const dueTodayTasks = activeTasks.filter(t => {
    const dueMoment = getDueDateMoment(t.dueDate);
    if (!dueMoment) return false;
    return dueMoment.isSame(moment(), 'day');
  });

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="container.xl" py={6}>
        <VStack align="stretch" spacing={6}>
          {/* Header */}
          <Box bg={headerBG} p={6} borderRadius="xl" shadow="sm">
            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={1}>
                <Heading size="lg">
                  {isViewingAll 
                    ? '✓ All Users\' Tasks'
                    : selectedUsers.length > 0 
                      ? `✓ ${selectedUsers.length === 1 
                          ? `${selectedUsers[0].label}'s Tasks` 
                          : `${selectedUsers.length} Users' Tasks`}`
                      : '✓ My Tasks'}
                </Heading>
                <Text color="gray.600" fontSize="sm">
                  {isViewingAll
                    ? 'Viewing all tasks across all users'
                    : selectedUsers.length > 0 
                      ? selectedUsers.length === 1 
                        ? 'Viewing another user\'s tasks' 
                        : `Viewing tasks for: ${selectedUsers.map(u => u.label).join(', ')}`
                      : 'Manage your tasks and to-dos'}
                </Text>
              </VStack>
                <Button
                  leftIcon={<FiPlus />}
                  colorScheme="blue"
                  onClick={() => {
                    setEditingTask(null);
                    onCreateOpen();
                  }}
                  shadow="sm"
                >
                  Add Task
                </Button>
            </HStack>

            {/* SuperAdmin User Filter */}
            {SuperAdmin && (
              <Box mt={4} p={4} bg={filterBG} borderRadius="md">
                <FormControl maxW="400px">
                  <FormLabel fontSize="xs" mb={1} textTransform="uppercase" fontWeight="semibold" color="gray.600">
                    View Tasks For
                  </FormLabel>
                  <Select
                    isMulti
                    options={userSelectOptions}
                    placeholder="My Tasks"
                    value={selectedUsers}
                    onChange={setSelectedUsers}
                    closeMenuOnSelect={false}
                    size="sm"
                    chakraStyles={{
                      container: (provided) => ({ ...provided, bg: 'white', borderRadius: 'md' }),
                      multiValue: (provided) => ({ ...provided, bg: 'blue.100' }),
                    }}
                  />
                </FormControl>
              </Box>
            )}

            {/* Quick Stats */}
            <Grid templateColumns="repeat(4, 1fr)" gap={4} mt={6}>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Total Tasks</StatLabel>
                  <StatNumber>{activeTasks.length}</StatNumber>
                  <StatHelpText>{completedTasks.length} completed</StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Due Today</StatLabel>
                  <StatNumber color={dueTodayTasks.length > 0 ? 'orange.500' : 'green.500'}>
                    {dueTodayTasks.length}
                  </StatNumber>
                  <StatHelpText>
                    {dueTodayTasks.length > 0 ? 'need attention' : 'all clear'}
                  </StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Overdue</StatLabel>
                  <StatNumber color={overdueTasks.length > 0 ? 'red.500' : 'green.500'}>
                    {overdueTasks.length}
                  </StatNumber>
                  <StatHelpText>
                    {overdueTasks.length > 0 ? 'take action' : 'on track'}
                  </StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Completion Rate</StatLabel>
                  <StatNumber>
                    {tasks.length > 0 
                      ? Math.round((completedTasks.length / tasks.length) * 100)
                      : 0}%
                  </StatNumber>
                  <StatHelpText>all time</StatHelpText>
                </Stat>
              </GridItem>
            </Grid>
          </Box>

          {/* A failed fetch must not look like an empty task list */}
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Couldn't load your tasks</AlertTitle>
                <AlertDescription fontSize="sm">{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Task List */}
          <TaskList
            tasks={tasks}
            onComplete={handleCompleteTask}
            onReopen={reopenTask}
            onEdit={handleTaskClick}
            onDelete={handleDeleteTask}
            onClick={handleTaskClick}
            getUserName={getAssignedUserName}
            showAssignee={isViewingAll || selectedUsers.length > 1}
          />
        </VStack>

        {/* Task Detail Modal */}
        <TaskDetailModal
          isOpen={isDetailOpen}
          onClose={onDetailClose}
          task={selectedTask}
          onEdit={handleEditTask}
          onDelete={handleDeleteTask}
          onComplete={handleCompleteTask}
          onReopen={reopenTask}
          assignedUserName={selectedTask ? getAssignedUserName(selectedTask.assignedTo) : null}
          createdByUserName={selectedTask ? getCreatedByUserName(selectedTask.createdBy) : null}
        />

        {/* Task Create/Edit Modal */}
        <UnifiedEventModal
          isOpen={isCreateOpen}
          onClose={handleCloseCreateModal}
          onSave={handleCloseCreateModal}
          initialEventType="task"
          editingTask={editingTask}
          crmUsers={crmUsers}
        />

        {/* Post-completion "log an activity?" prompt */}
        <TaskCompletionActivityPrompt
          isOpen={isActivityPromptOpen}
          onClose={handleActivityPromptClose}
          task={completedTaskForPrompt}
          onCreateTask={(taskData) => createTask(taskData)}
        />
      </Container>
    </Box>
  );
}

